import React from "react";
import phoneNumberImg from "assets/images/svg/security_and_login_phone.svg";
import "react-phone-input-2/lib/style.css";
import FlagComponent from "components/flag/flag.component";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useModal } from "hooks";
import VerifyPhoneDialog from "pages/security-login/components/verify-phone.dialog";
import { validatePhone } from "helpers/data";
import { parsePhoneNumber, PhoneNumber } from "awesome-phonenumber";
import { useQuery, useQueryClient } from "react-query";
import { client } from "helpers/client";
import ErrorMessage from "components/error/error";
import { isBlocked, isExpired, Mobile } from "pages/security-login/security-login.service";
import { useAuthContext } from "store/auth";
import { useStatus } from "hooks/use-status";
import { CounterInfoComponent } from "pages/security-login/components/counter-info.component";
import { Spinner } from "react-bootstrap";
import CanLoadGuard, { CanLoadPage } from "components/private-route/can-load.guard";

const PhoneNumberPage = () => {
  const { t } = useTranslation("security");
  const [{ user }] = useAuthContext();
  const queryClient = useQueryClient();
  const { isLoading, load, done, stop } = useStatus();
  const {
    register,
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { errors, isDirty },
    watch
  } = useForm();
  const [showVerifyPhoneNumber, openVerifyPhoneNumber, closeVerifyPhoneNumber] = useModal(false);
  const {
    data: mobiles,
    isSuccess,
    isError
  } = useQuery(
    "mobiles-data",
    () =>
      client<Mobile[]>("/security/getMobiles").then((r) => {
        if (r.response) return r.response;
        else return Promise.reject();
      }),
    {
      onSuccess(response) {
        const alternativeMobile = response.find((m) => !m.isPrimary);
        const phone = alternativeMobile ? parsePhoneNumber(alternativeMobile?.mobile) : null;

        reset({
          alternativeMobile: alternativeMobile?.mobile,
          countryCode: phone?.getCountryCode().toString()
        });
      }
    }
  );

  const primaryMobile = mobiles?.find((m) => m.isPrimary);
  const alternativeMobile = mobiles?.find((m) => !m.isPrimary);
  const isBothVerified = primaryMobile?.isVerified && alternativeMobile?.isVerified;

  async function handleVerifyAlternativeMobile(data: any) {
    load();
    if (isExpired(alternativeMobile?.expiryDate)) {
      done();
      return openVerifyPhoneNumber();
    }

    const phone = validatePhone(data.alternativeMobile, data.countryCode) as PhoneNumber;

    const { response } = await client("/security/verifyAlternativeMobile", {
      data: {
        customerId: user.Id,
        mobileId: alternativeMobile?.mobileId,
        mobile: phone.getNumber(),
        mobileCountryCode: data.countryCode
      }
    });
    if (response) {
      done();
      return queryClient.invalidateQueries("mobiles-data").then(() => openVerifyPhoneNumber());
    } else stop();
  }

  async function handleMakePhonePrimary() {
    const { response } = await client(`/security/makeMobilePrimary/${alternativeMobile?.mobileId}`, { method: "POST" });
    if (response) return queryClient.invalidateQueries("mobiles-data");
  }

  return (
    <CanLoadPage>
      <div className="phoneNumberPage">
        <div className="row">
          {isError && <ErrorMessage />}
          {isSuccess && (
            <form onSubmit={handleSubmit(handleVerifyAlternativeMobile)} className="col-lg-4 col-md-8">
              <div className="row">
                <div className="col-2">
                  <FlagComponent disabled value="20" />
                </div>
                <div className="col-10">
                  <div className="input-div">
                    <label className="lapel-input readonly" htmlFor="mobile">
                      <span>{t("mobile-number")}:</span>
                      <input readOnly disabled value={primaryMobile?.mobile} type="text" id="mobile" />
                      <span className="primary">{t("primary")}</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-2">
                  <Controller
                    name="countryCode"
                    control={control}
                    defaultValue="20"
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FlagComponent
                        buttonClass={fieldState.error ? "error" : ""}
                        onChange={(v) => {
                          setValue("alternativeMobile", `+${v}`, { shouldDirty: true });
                          field.onChange(v);
                        }}
                        onBlur={field.onBlur}
                        value={field.value}
                      />
                    )}
                  />
                </div>
                <div className="col-10">
                  <div className="input-div">
                    <label
                      className={"lapel-input " + (errors.alternativePhoneNumber ? "error" : "")}
                      htmlFor="alternativePhoneNumber"
                    >
                      <span>{t("alternative-mobile-number")}:</span>
                      <input
                        type="text"
                        id="alternativeMobile"
                        disabled={isExpired(alternativeMobile?.expiryDate) || isBlocked(alternativeMobile?.blockTillDate)}
                        {...register("alternativeMobile", {
                          required: "errors:required",
                          onChange: (e) => setValue("countryCode", e.target.value, { shouldValidate: true, shouldDirty: true }),
                          validate: () =>
                            !!validatePhone(watch("alternativeMobile"), watch("countryCode")) || "errors:invalidFormat"
                        })}
                      />
                      {alternativeMobile?.isVerified && !isDirty && (
                        <span className="verify" onClick={handleMakePhonePrimary}>
                          {t("make-primary")}
                        </span>
                      )}
                    </label>
                    {/* @ts-ignore*/}
                    <span className="text-error">{t(errors.alternativeMobile?.message)}</span>
                  </div>
                </div>
              </div>
              <CanLoadGuard>
                <button className="btn-blue" type="submit" disabled={isLoading || (!isDirty && isBothVerified)}>
                  {isLoading && <Spinner animation="border" size="sm" />}
                  {t("verify-mobile")}
                </button>
                {isExpired(alternativeMobile?.expiryDate) && (
                  <CounterInfoComponent
                    message={t("we-sent-code-to-your-number")}
                    date={alternativeMobile?.expiryDate}
                    queryKey="mobiles-data"
                  />
                )}
                {isBlocked(alternativeMobile?.blockTillDate) && !isExpired(alternativeMobile?.expiryDate) && (
                  <CounterInfoComponent
                    message={t("you-have-been-blocked")}
                    date={alternativeMobile?.blockTillDate}
                    queryKey="mobiles-data"
                  />
                )}
              </CanLoadGuard>
            </form>
          )}

          <div className="col-lg-8 col-md-4">
            <img className="img-fluid background" src={phoneNumberImg} alt="phoneNumberImg" />
          </div>
        </div>
        <VerifyPhoneDialog
          mobileId={alternativeMobile?.mobileId}
          block={alternativeMobile?.blockTillDate}
          onResend={() => handleVerifyAlternativeMobile(getValues())}
          show={showVerifyPhoneNumber}
          onHide={closeVerifyPhoneNumber}
          expire={alternativeMobile?.expiryDate}
        />
      </div>
    </CanLoadPage>
  );
};

export default PhoneNumberPage;
